<script setup lang="ts">
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

// eslint-disable-next-line
dayjs.extend(duration);

interface Props {
  tournament: TTournament;
  variant?: 'disponiveis' | 'participando' | 'finalizados';
}

const props = withDefaults(defineProps<Props>(), { variant: 'disponiveis' });

const currentTime = ref(dayjs());
const tournamentDetails = ref<TTournamentDetailed>();
const { loadTournamentInfo } = useSmarticoTournaments();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();
const remainingTimes = ref<Record<string, string>>({});

const filterTournamentNameTitle = computed(() => {
  return props.tournament.name;
});

const prizeAmount = computed(() => {
  let prize = 'Misto';

  if (props.tournament.prize_pool_short) {
    prize = `${props.tournament.prize_pool_short}`;
  }

  return prize;
});

function formatPlayerName(username: string): string {
  if (!username) return '***';
  if (username.length <= 4) return username;

  return username.substring(0, 4) + '***';
}

function formatPrize(position: number): string {
  if (!tournamentDetails.value || !tournamentDetails.value.prizes)
    return 'R$0,00';

  const prize = tournamentDetails.value.prizes.find(
    (p: any) => position >= p.place_from && position <= p.place_to,
  );

  return prize ? prize.name : 'R$0,00';
}

function formatNumber(n: number | null | undefined) {
  if (n === null || n === undefined) return '0';
  try {
    return new Intl.NumberFormat('pt-BR').format(n);
  } catch {
    return String(n);
  }
}

const formattedParticipantCount = computed(() =>
  formatNumber(participantCount.value),
);

const participantCount = computed(() => {
  return (
    tournamentDetails.value?.registration_count ??
    props.tournament.registration_count ??
    0
  );
});

const tournamentImageUrl = computed(() => {
  if (import.meta.client && window.innerWidth < 768) {
    return props.tournament.image2_mobile || props.tournament.image2;
  }

  return props.tournament.image2 || props.tournament.image2_mobile;
});

const ribbonText = computed<string | null>(() => {
  const r = String(props.tournament.ribbon || '').trim();

  return r || null;
});

// Button/Badge variant handling
const btnLabel = computed(() => {
  const v = props.variant || 'disponiveis';

  if (v === 'participando') return 'VER TORNEIO';
  if (v === 'finalizados') return 'VER RESULTADOS';

  return 'PARTICIPAR AGORA';
});

const btnClass = computed(() => {
  const v = props.variant || 'disponiveis';

  if (v === 'participando') return 'btn--participando';
  if (v === 'finalizados') return 'btn--finalizados';

  return 'btn--disponiveis';
});

const remainingTime = computed(() => {
  const instId = props.tournament.instance_id ?? props.tournament.tournament_id;

  // If tournament is finished, show zeroed time
  if (props.variant === 'finalizados') return '00D 00:00:00';

  // Prefer the precomputed reactive remainingTimes (updated by interval)
  if (instId !== undefined && instId !== null) {
    const key = String(instId);
    const pre = remainingTimes.value[key];

    if (pre) return pre;
  }

  const end =
    props.tournament.end_time ?? tournamentDetails.value?.end_time ?? null;

  if (!end) return '—';

  return formatTimeRemaining(end as number);
});

const displayCounterText = computed(() => {
  if (props.variant === 'finalizados') {
    return 'FINALIZADO';
  } else {
    return remainingTime.value;
  }
});

const tournamentId = computed(
  () => props.tournament.instance_id ?? props.tournament.tournament_id ?? '',
);

function viewTournament() {
  if (!isAuthenticated.value) {
    openLogin();

    return;
  }
  navigateTo(localeLink('/vip/tournaments/' + tournamentId.value));
}

async function loadTournamentDetails() {
  if (!props.tournament.instance_id) {
    console.error('Tournament instance ID not available');

    return undefined;
  }

  try {
    if (import.meta.client && window && window._smartico) {
      let details;
      const maxRetries = 10;

      const retryApiCall = async (
        apiCall: () => Promise<any>,
        maxRetries: number,
      ) => {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            const result = await apiCall();

            if (result) {
              return result;
            }
          } catch (err: any) {
            const msg = err?.message || String(err);

            // Log the failed attempt
            console.warn(`Retry attempt ${attempt} failed:`, err);

            // If the error indicates an internal Smartico null access (non-retryable), abort early
            if (
              msg.includes('tournamentLobbyInfo') ||
              msg.includes('Cannot read properties of null') ||
              msg.includes('Cannot read properties of undefined')
            ) {
              throw err;
            }

            if (attempt === maxRetries) {
              throw err;
            }

            // Wait before retrying, increasing delay with each attempt
            await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
          }
        }

        throw new Error('API call failed after maximum retries');
      };

      if (isAuthenticated.value && props.tournament.is_user_registered) {
        details = await retryApiCall(
          () =>
            window._smartico.api.getTournamentInstanceInfo(
              props.tournament.instance_id,
            ),
          maxRetries,
        );

        if (details && details.instance_id !== props.tournament.instance_id) {
          details = await retryApiCall(
            () =>
              window._smartico.api.getTournamentInstanceInfo(
                props.tournament.instance_id,
              ),
            maxRetries,
          );
        }

        return details;
      } else {
        details = await retryApiCall(
          () =>
            window._smartico
              .vapi('EN')
              .getTournamentInstanceInfo(props.tournament.instance_id),
          maxRetries,
        );

        return details;
      }
    } else {
      const details = await loadTournamentInfo(props.tournament.instance_id);

      return details;
    }
  } catch (error) {
    console.error(
      `Error loading tournament details ${props.tournament.instance_id}:`,
      error,
    );

    return undefined;
  }
}

async function startTournamentDetailsLoading() {
  const details = await loadTournamentDetails();

  if (details) {
    tournamentDetails.value = details;
  }
  currentTime.value = dayjs();
}

function formatTimeRemaining(end_time: number): string {
  const now = Date.now();
  let diff = end_time - now;

  if (diff <= 0) return '—';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  diff -= days * 1000 * 60 * 60 * 24;

  const hours = Math.floor(diff / (1000 * 60 * 60));

  diff -= hours * 1000 * 60 * 60;

  const minutes = Math.floor(diff / (1000 * 60));

  diff -= minutes * 1000 * 60;

  const seconds = Math.floor(diff / 1000);

  const pad = (n: number) => n.toString().padStart(2, '0');

  return `${pad(days)}D ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

const tryLoadTournamentDetails = (maxRetries = 5, delayMs = 1000) => {
  let retries = 0;

  const attempt = async (): Promise<void> => {
    if (window && window._smartico) {
      await startTournamentDetailsLoading();

      return;
    }

    if (retries < maxRetries) {
      retries++;
      await new Promise((resolve) => setTimeout(resolve, delayMs));

      return attempt();
    }

    console.warn('Falha ao carregar _smartico após tentativas máximas');
  };

  return attempt();
};

let intervalId: ReturnType<typeof setInterval> | undefined;

onMounted(() => {
  tryLoadTournamentDetails();

  if (props.tournament?.end_time) {
    intervalId = setInterval(() => {
      if (props.tournament.end_time > Date.now()) {
        remainingTimes.value[props.tournament.instance_id] =
          formatTimeRemaining(props.tournament.end_time);
      }
    }, 1000);
  }
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = undefined;
  }
});
</script>

<template>
  <div
    class="max-w-[400px] mx-auto select-none w-full min-w-[340px] box-border"
  >
    <div
      class="bg-[#010612] rounded-[7.4px] p-3 border border-solid border-[#1D2329] cursor-pointer hover:border-[#2D3339] transition-colors"
      @click="viewTournament"
    >
      <div
        class="flex gap-2"
        :class="{
          grayscale: tournament.is_cancelled || tournament.is_finished,
        }"
      >
        <div
          class="relative w-[125px] h-[125px] rounded-[7.4px] overflow-hidden z-10"
        >
          <!-- Div de fundo (simula a borda) com 2px a mais em cada lado -->
          <div
            class="absolute -top-[1px] -left-[1px] -right-[1px] -bottom-[1px] rounded-[7.4px] bg-gradient-to-r from-[#ff704a] to-[#ff3400] z-0"
          ></div>

          <div class="relative rounded-[7.4px] z-10">
            <NuxtImg
              :alt="`Tournament-${tournamentId}`"
              class="w-[125px] h-[125px] object-cover rounded-[7.4px] border-[0.65px] border-solid border-[#ff3400]"
              :draggable="false"
              :src="tournamentImageUrl"
            />

            <!-- Ribbon (laranja) -->
            <div
              v-if="ribbonText"
              class="absolute -top-[1px] -left-[1px] bg-gradient-to-r from-[#ff704a] to-[#ff3400] text-white text-[8px] font-semibold px-[7.4px] py-[3.7px] rounded-br-[7.4px] z-20"
            >
              {{ ribbonText }}
            </div>

            <!-- Badge de jogadores online no canto inferior direito (restaurado) -->
            <div
              v-if="participantCount > 0"
              class="absolute -bottom-[1px] -right-[1px] bg-gradient-to-r from-[#ff704a] to-[#ff3400] text-white text-[8px] font-semibold px-[7.4px] py-[3.7px] rounded-tl-[7.4px] flex items-center gap-1 shadow-lg z-20"
            >
              <NuxtIcon
                class="text-[10px] text-white"
                name="players"
              />
              <span>{{ formattedParticipantCount }}</span>
            </div>

            <!-- contador removido do canto da imagem; será exibido ao lado do botão -->
          </div>
        </div>

        <!-- Conteúdo ao lado da imagem -->
        <div class="min-w-0 flex-1">
          <span
            class="block w-full text-white text-start text-[14.796px] font-bold leading-[19.235px] truncate overflow-ellipsis"
            >{{ filterTournamentNameTitle }}</span
          >
          <!-- Ranking section -->
          <div
            v-if="
              tournamentDetails?.players && tournamentDetails.players.length > 0
            "
            class="mt-3 flex-1"
          >
            <div
              v-for="(player, idx) in tournamentDetails.players.slice(0, 3)"
              :key="idx"
              class="flex items-center gap-1 text-white text-[10px] px-2 py-2 rounded-[4px]"
              :class="{
                'bg-[#D6ED16] text-black': player.is_me,
                'bg-[#FFFFFF1A]': idx % 2 === 0 && !player.is_me,
              }"
            >
              <NuxtImg
                v-if="player.position <= 3"
                :alt="`${player.position}º`"
                class="w-[14px] h-[14px]"
                :draggable="false"
                :src="
                  getAssetsUrlForCDN(
                    `/assets/gamification/t-place-${player.position}.png`,
                  )
                "
              />
              <span
                v-else
                class="w-[14px] text-center font-bold"
                >{{ player.position }}</span
              >
              <span
                class="font-normal truncate text-[10px] flex-1 min-w-0 uppercase"
                >{{ formatPlayerName(player.public_username || '***') }}</span
              >
              <span
                class="font-normal text-white text-[9px] ml-1 max-w-[110px] truncate block"
                >{{ formatPrize(player.position) }}</span
              >
            </div>
          </div>
        </div>
      </div>
      <!-- Info rows -->
      <div class="mt-2.5 space-y-2 mx-auto">
        <div
          class="flex flex-col font-black justify-between items-center self-stretch min-w-0 [background:rgba(0,201,255,0.10)] backdrop-blur-[6.5873823165893555px] px-2.5 py-1.5 rounded-[6.587px]"
        >
          <div
            class="text-white text-[10px] font-normal leading-[10.54px] tracking-[0.632px]"
            >PRÊMIO TOTAL</div
          >
          <div
            class="text-[#00C9FF] text-center text-[20px] tracking-[-0.632px] truncate w-full"
            >{{ prizeAmount }}</div
          >
        </div>
      </div>
      <!-- Botão Participar -->
      <div
        class="mt-2 p-1 flex justify-between items-center gap-[12px] self-stretch [background:rgba(255,255,255,0.05)] rounded-[6.587px]"
      >
        <div
          class="flex items-center justify-center gap-2 px-2 py-1 rounded-md text-white text-[12px] font-semibold"
        >
          <NuxtIcon
            class="text-[13px] text-white"
            name="tournament-single/clock"
          />
          <span class="text-[10px] font-bold w-[85px]">{{
            displayCounterText
          }}</span>
        </div>

        <button
          :class="[
            'flex-1 flex h-[33px] justify-center items-center gap-[3.699px] self-stretch px-[7.398px] py-0 rounded-[4.439px]',
            btnClass,
          ]"
          @click.stop="viewTournament"
        >
          <span class="text-white text-[12px] font-extrabold">{{
            btnLabel
          }}</span>
          <NuxtIcon
            class="text-[12px] text-white"
            name="mission/button/participar"
          />
        </button>
      </div>
    </div>
  </div>
</template>
<style scoped>
.ribbon-container {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translate(-50%, -50%);
  z-index: 10;
  min-width: 60px;
  width: auto;
  height: 16px;
  padding: 0 4px;
  background: radial-gradient(
    ellipse 138% 168.3% at 36.76% -16.67%,
    #ff704a 0%,
    #ff3400 100%
  );
  border-radius: 4px;
  outline: 1px solid rgb(252 165 165);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Variant button styles */
.btn--disponiveis {
  background: radial-gradient(
    168.3% 138% at 36.76% -16.67%,
    #ff704a 0%,
    #ff3400 100%
  );
  box-shadow: 0 4px 26.9px 0 rgba(255, 69, 12, 0.21);
}

.btn--participando {
  background-color: rgb(2 7 22 / var(--tw-bg-opacity, 1)) /* #020716 */ !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: none;
}

.btn--finalizados {
  background: linear-gradient(90deg, #6b7280 0%, #374151 100%);
  box-shadow: none;
  opacity: 0.95;
}

/* Ensure text inside variant buttons stays readable */
.btn--disponiveis > span,
.btn--participando > span,
.btn--finalizados > span {
  color: #ffffff;
}
</style>
